import React from "react";
import {withStyles} from "material-ui/styles";
import {
  Activity,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  RefreshCw,
  XCircle,
  Hash,
  Timer,
  ArrowRightLeft,
  Loader
} from "lucide-react";
import {
  Button,
  Cell,
  Column,
  Flex,
  Heading,
  Item,
  Picker,
  Provider,
  Row,
  TableBody,
  TableHeader,
  TableView,
  Text,
  View
} from "@adobe/react-spectrum";
import { customSpectrumTheme, SPECTRUM_THEME_CLASS } from "../../themes/spectrum-theme";
import styles from "./styles";
import Content from "../ui/content";

const JOB_TYPE_LABELS = {
  0: "Subscription Price Update",
  SubscriptionPriceUpdate: "Subscription Price Update",
};

const STATUS_CONFIG = {
  Running: {label: "Running", color: "#004578", bg: "#e3f2fd", icon: Loader},
  Completed: {label: "Completed", color: "#2e7d32", bg: "#e8f5e9", icon: CheckCircle2},
  Failed: {label: "Failed", color: "#c62828", bg: "#fce4ec", icon: XCircle},
  0: {label: "Running", color: "#004578", bg: "#e3f2fd", icon: Loader},
  1: {label: "Completed", color: "#2e7d32", bg: "#e8f5e9", icon: CheckCircle2},
  2: {label: "Failed", color: "#c62828", bg: "#fce4ec", icon: XCircle},
};

class JobLogs extends React.Component {

  formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  formatDuration = (ms) => {
    if (ms == null) return "-";
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.Running;
  };

  renderStatusBadge = (status) => {
    const config = this.getStatusConfig(status);
    const StatusIcon = config.icon;
    return (
      <span style={{
        backgroundColor: config.bg,
        color: config.color,
        padding: "4px 10px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
      }}>
        <StatusIcon size={12}/>
        {config.label}
      </span>
    );
  };

  renderJobLogsList = () => {
    const {
      classes,
      jobLogs,
      onRowClick,
      onPageChange,
      onJobTypeFilterChange,
      onRefresh,
      onBackToDashboard,
      currentPage,
      jobTypeFilter,
      translate,
    } = this.props;

    const {data, pagination, loading, error} = jobLogs;

    return (
      <View>
        {/* Header */}
        <Flex justifyContent="space-between" alignItems="center" marginBottom="size-200">
          <Flex alignItems="center" gap="size-200">
            <Button variant="secondary" onPress={onBackToDashboard}>
              <ArrowLeft size={16} style={{marginRight: "6px"}}/>
              Dashboard
            </Button>
            <Heading level={1} UNSAFE_style={{margin: 0}}>
              Background Job Logs
            </Heading>
          </Flex>
          <Flex gap="size-100" alignItems="center">
            <Picker
              label="Job Type"
              selectedKey={jobTypeFilter != null ? String(jobTypeFilter) : "all"}
              onSelectionChange={(key) => onJobTypeFilterChange(key === "all" ? null : parseInt(key, 10))}
              width="size-2400"
            >
              <Item key="all">All Types</Item>
              <Item key="0">Subscription Price Update</Item>
            </Picker>
            <Button variant="secondary" onPress={onRefresh}>
              <RefreshCw size={16} style={{marginRight: "6px"}}/>
              Refresh
            </Button>
          </Flex>
        </Flex>

        {/* Loading State */}
        {loading && (
          <View padding="size-500">
            <Flex justifyContent="center" alignItems="center" gap="size-100">
              <Loader size={20} className="spin"/>
              <Text>Loading job logs...</Text>
            </Flex>
          </View>
        )}

        {/* Error State */}
        {error && (
          <View padding="size-200" marginBottom="size-200">
            <div className={classes.errorBox}>
              <div className={classes.errorTitle}>Error loading job logs</div>
              <div className={classes.errorMessage}>{error}</div>
            </div>
          </View>
        )}

        {/* Table */}
        {!loading && data && (
          <View>
            <TableView aria-label="Background Job Logs" onAction={(key) => onRowClick(key)}>
              <TableHeader>
                <Column>Job Name</Column>
                <Column>Status</Column>
                <Column>Started</Column>
                <Column>Duration</Column>
                <Column>Processed</Column>
                <Column>Changed</Column>
                <Column>Failed</Column>
                <Column>Triggered By</Column>
                <Column>Action</Column>
              </TableHeader>
              <TableBody>
                {data.map((log) => (
                  <Row key={log.id}>
                    <Cell>
                      <Flex alignItems="center" gap="size-100">
                        <Activity size={14} color="#005795"/>
                        <Text>{log.jobName || JOB_TYPE_LABELS[log.jobType] || "Unknown"}</Text>
                      </Flex>
                    </Cell>
                    <Cell>{this.renderStatusBadge(log.status)}</Cell>
                    <Cell>
                      <Text UNSAFE_style={{fontSize: "12px"}}>
                        {this.formatDateTime(log.startedAt)}
                      </Text>
                    </Cell>
                    <Cell>
                      <Flex alignItems="center" gap="size-50">
                        <Timer size={12} color="#888"/>
                        <Text UNSAFE_style={{fontSize: "12px"}}>
                          {this.formatDuration(log.durationMs)}
                        </Text>
                      </Flex>
                    </Cell>
                    <Cell>{log.totalItemsProcessed ?? "-"}</Cell>
                    <Cell>
                      <Text UNSAFE_style={{
                        color: log.itemsChanged > 0 ? "#2e7d32" : "#888",
                        fontWeight: log.itemsChanged > 0 ? 600 : 400
                      }}>
                        {log.itemsChanged ?? "-"}
                      </Text>
                    </Cell>
                    <Cell>
                      <Text UNSAFE_style={{
                        color: log.itemsFailed > 0 ? "#c62828" : "#888",
                        fontWeight: log.itemsFailed > 0 ? 600 : 400
                      }}>
                        {log.itemsFailed ?? "-"}
                      </Text>
                    </Cell>
                    <Cell>
                      <Text UNSAFE_style={{fontSize: "12px"}}>
                        {log.triggeredBy || "System"}
                      </Text>
                    </Cell>
                    <Cell>
                      <button
                        className={classes.actionButton}
                        onClick={() => onRowClick(log.id)}
                      >
                        View Details
                      </button>
                    </Cell>
                  </Row>
                ))}
              </TableBody>
            </TableView>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <Flex
                gap="size-200"
                alignItems="center"
                justifyContent="center"
                marginTop="size-200"
              >
                <Button
                  variant="secondary"
                  isDisabled={currentPage === 1}
                  onPress={() => onPageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                <Text>
                  Page {currentPage} of {pagination.totalPages}
                  {" "}({pagination.totalCount} total)
                </Text>
                <Button
                  variant="secondary"
                  isDisabled={currentPage === pagination.totalPages}
                  onPress={() => onPageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </Flex>
            )}
          </View>
        )}

        {/* Empty State */}
        {!loading && data && data.length === 0 && (
          <View padding="size-500">
            <Flex direction="column" alignItems="center" gap="size-200">
              <Clock size={48} color="#ccc"/>
              <Heading level={3} UNSAFE_style={{color: "#888"}}>
                No job logs found
              </Heading>
              <Text UNSAFE_style={{color: "#aaa"}}>
                Background job execution logs will appear here once jobs have run.
              </Text>
            </Flex>
          </View>
        )}
      </View>
    );
  };

  renderJobLogDetail = () => {
    const {
      classes,
      jobLogDetails,
      onBackToList,
      onRefresh,
    } = this.props;

    const {data, loading, error} = jobLogDetails;

    if (loading) {
      return (
        <View padding="size-500">
          <Flex justifyContent="center" alignItems="center" gap="size-100">
            <Loader size={20}/>
            <Text>Loading job details...</Text>
          </Flex>
        </View>
      );
    }

    if (error) {
      return (
        <View>
          <Button variant="secondary" onPress={onBackToList}>
            <ArrowLeft size={16} style={{marginRight: "6px"}}/>
            Back to List
          </Button>
          <div className={classes.errorBox} style={{marginTop: "16px"}}>
            <div className={classes.errorTitle}>Error loading job details</div>
            <div className={classes.errorMessage}>{error}</div>
          </div>
        </View>
      );
    }

    if (!data) return null;

    const statusConfig = this.getStatusConfig(data.status);

    return (
      <View>
        {/* Header */}
        <Flex justifyContent="space-between" alignItems="center" marginBottom="size-300">
          <Flex alignItems="center" gap="size-200">
            <Button variant="secondary" onPress={onBackToList}>
              <ArrowLeft size={16} style={{marginRight: "6px"}}/>
              Back to List
            </Button>
            <Heading level={1} UNSAFE_style={{margin: 0}}>
              {data.jobName || JOB_TYPE_LABELS[data.jobType] || "Job Details"}
            </Heading>
            {this.renderStatusBadge(data.status)}
          </Flex>
          <Button variant="secondary" onPress={onRefresh}>
            <RefreshCw size={16} style={{marginRight: "6px"}}/>
            Refresh
          </Button>
        </Flex>

        {/* Summary Metrics */}
        <div className={classes.metricsRow}>
          <div className={classes.metricBox}>
            <div className={classes.metricBoxValue}>{data.totalItemsProcessed ?? "-"}</div>
            <div className={classes.metricBoxLabel}>Items Processed</div>
          </div>
          <div className={classes.metricBox}>
            <div className={classes.metricBoxValue} style={{color: "#2e7d32"}}>
              {data.itemsChanged ?? "-"}
            </div>
            <div className={classes.metricBoxLabel}>Items Changed</div>
          </div>
          <div className={classes.metricBox}>
            <div className={classes.metricBoxValue} style={{color: data.itemsFailed > 0 ? "#c62828" : "#888"}}>
              {data.itemsFailed ?? "-"}
            </div>
            <div className={classes.metricBoxLabel}>Items Failed</div>
          </div>
          <div className={classes.metricBox}>
            <div className={classes.metricBoxValue}>
              {this.formatDuration(data.durationMs)}
            </div>
            <div className={classes.metricBoxLabel}>Duration</div>
          </div>
        </div>

        {/* Detail Info */}
        <div className={classes.detailCard}>
          <Heading level={3} marginTop={0} marginBottom="size-200">
            Execution Details
          </Heading>
          <div className={classes.detailGrid}>
            <div className={classes.detailItem}>
              <span className={classes.detailLabel}>Job Type</span>
              <span className={classes.detailValue}>
                {JOB_TYPE_LABELS[data.jobType] || data.jobType}
              </span>
            </div>
            <div className={classes.detailItem}>
              <span className={classes.detailLabel}>Started At</span>
              <span className={classes.detailValue}>{this.formatDateTime(data.startedAt)}</span>
            </div>
            <div className={classes.detailItem}>
              <span className={classes.detailLabel}>Completed At</span>
              <span className={classes.detailValue}>{this.formatDateTime(data.completedAt)}</span>
            </div>
            <div className={classes.detailItem}>
              <span className={classes.detailLabel}>Triggered By</span>
              <span className={classes.detailValue}>{data.triggeredBy || "System (Hangfire)"}</span>
            </div>
            <div className={classes.detailItem}>
              <span className={classes.detailLabel}>Log ID</span>
              <span className={classes.detailValue}>#{data.id}</span>
            </div>
          </div>
        </div>

        {/* Error Details (if failed) */}
        {data.errorMessage && (
          <div className={classes.detailCard}>
            <Heading level={3} marginTop={0} marginBottom="size-200"
                     UNSAFE_style={{color: "#c62828"}}>
              <Flex alignItems="center" gap="size-100">
                <AlertTriangle size={18}/>
                Error Details
              </Flex>
            </Heading>
            <div className={classes.errorBox}>
              <div className={classes.errorTitle}>{data.errorMessage}</div>
              {data.errorStackTrace && (
                <details style={{marginTop: "8px"}}>
                  <summary style={{cursor: "pointer", fontSize: "12px", color: "#666"}}>
                    Stack Trace
                  </summary>
                  <div className={classes.errorMessage} style={{marginTop: "8px"}}>
                    {data.errorStackTrace}
                  </div>
                </details>
              )}
            </div>
          </div>
        )}

        {/* Per-Subscription Results Table */}
        {data.resultDetail && this.renderSubscriptionResults(data.resultDetail)}
      </View>
    );
  };

  renderSubscriptionResults = (resultDetail) => {
    const {classes} = this.props;

    // resultDetail should be the deserialized object from SubscriptionPriceUpdateJob
    // Expected shape: { BatchId, ProcessedAt, TotalSubscriptions, Updated, Unchanged, Errors, Results: [...] }
    const results = resultDetail.Results || resultDetail.results || [];

    if (results.length === 0) return null;

    return (
      <div className={classes.detailCard}>
        <Heading level={3} marginTop={0} marginBottom="size-200">
          <Flex alignItems="center" gap="size-100">
            <ArrowRightLeft size={18} color="#005795"/>
            Subscription Update Results
          </Flex>
        </Heading>

        {/* Summary bar */}
        <Flex gap="size-200" marginBottom="size-200" wrap>
          <Text UNSAFE_style={{fontSize: "12px", color: "#666"}}>
            Total: <strong>{resultDetail.TotalSubscriptions ?? resultDetail.totalSubscriptions ?? results.length}</strong>
          </Text>
          <Text UNSAFE_style={{fontSize: "12px", color: "#2e7d32"}}>
            Updated: <strong>{resultDetail.Updated ?? resultDetail.updated ?? 0}</strong>
          </Text>
          <Text UNSAFE_style={{fontSize: "12px", color: "#888"}}>
            Unchanged: <strong>{resultDetail.Unchanged ?? resultDetail.unchanged ?? 0}</strong>
          </Text>
          <Text UNSAFE_style={{fontSize: "12px", color: "#c62828"}}>
            Errors: <strong>{resultDetail.Errors ?? resultDetail.errors ?? 0}</strong>
          </Text>
        </Flex>

        <div className={classes.resultTableContainer}>
          <table className={classes.resultTable}>
            <thead>
            <tr>
              <th>#</th>
              <th>Organisation</th>
              <th>Stripe Sub ID</th>
              <th>Price Changed</th>
              <th>Devices</th>
              <th>Price</th>
              <th>Tier</th>
              <th>Stripe Updated</th>
              <th>Email Sent</th>
              <th>Error</th>
            </tr>
            </thead>
            <tbody>
            {results.map((r, idx) => {
              const orgId = r.OrganisationId || r.organisationId || "-";
              const stripeSubId = r.StripeSubscriptionId || r.stripeSubscriptionId || "-";
              const priceChanged = r.PriceChanged ?? r.priceChanged ?? false;
              const oldDevices = r.OldDeviceCount ?? r.oldDeviceCount;
              const newDevices = r.NewDeviceCount ?? r.newDeviceCount;
              const oldPrice = r.OldPriceInPence ?? r.oldPriceInPence;
              const newPrice = r.NewPriceInPence ?? r.newPriceInPence;
              const oldTier = r.OldTier ?? r.oldTier;
              const newTier = r.NewTier ?? r.newTier;
              const stripeUpdated = r.StripeUpdated ?? r.stripeUpdated ?? false;
              const emailSent = r.EmailSent ?? r.emailSent ?? false;
              const error = r.Error ?? r.error;

              return (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td style={{fontFamily: "monospace", fontSize: "11px"}}>{orgId}</td>
                  <td style={{fontFamily: "monospace", fontSize: "11px"}}>
                    {typeof stripeSubId === "string" && stripeSubId.length > 16
                      ? stripeSubId.substring(0, 16) + "…"
                      : stripeSubId}
                  </td>
                  <td>
                    {priceChanged ? (
                      <span style={{color: "#2e7d32", fontWeight: 600}}>Yes</span>
                    ) : (
                      <span style={{color: "#888"}}>No</span>
                    )}
                  </td>
                  <td>
                    {priceChanged && oldDevices != null
                      ? <span>{oldDevices} → {newDevices}</span>
                      : <span>{newDevices ?? "-"}</span>
                    }
                  </td>
                  <td>
                    {priceChanged && oldPrice != null ? (
                      <span>
                        £{(oldPrice / 100).toFixed(2)} → £{(newPrice / 100).toFixed(2)}
                      </span>
                    ) : (
                      <span>{newPrice != null ? `£${(newPrice / 100).toFixed(2)}` : "-"}</span>
                    )}
                  </td>
                  <td>
                    {priceChanged && oldTier
                      ? <span>{oldTier} → {newTier}</span>
                      : <span>{newTier || "-"}</span>
                    }
                  </td>
                  <td>
                    {stripeUpdated ? (
                      <CheckCircle2 size={14} color="#2e7d32"/>
                    ) : priceChanged ? (
                      <XCircle size={14} color="#c62828"/>
                    ) : (
                      <span style={{color: "#ccc"}}>-</span>
                    )}
                  </td>
                  <td>
                    {emailSent ? (
                      <CheckCircle2 size={14} color="#2e7d32"/>
                    ) : priceChanged ? (
                      <XCircle size={14} color="#c62828"/>
                    ) : (
                      <span style={{color: "#ccc"}}>-</span>
                    )}
                  </td>
                  <td>
                    {error ? (
                      <span style={{color: "#c62828", fontSize: "11px"}} title={error}>
                        <AlertTriangle size={12}/> {error.length > 30 ? error.substring(0, 30) + "…" : error}
                      </span>
                    ) : (
                      <span style={{color: "#ccc"}}>-</span>
                    )}
                  </td>
                </tr>
              );
            })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  render() {
    const {translate, selectedJobLogId} = this.props;

    return (
      <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
        <div>
          <Content translate={translate}>
            <View UNSAFE_style={{
              maxWidth: "1400px",
              margin: "0 auto",
              paddingTop: "60px",
            }}>
              <Flex direction="column" gap="size-200">
                {selectedJobLogId
                  ? this.renderJobLogDetail()
                  : this.renderJobLogsList()
                }
              </Flex>
            </View>
          </Content>
        </div>
      </Provider>
    );
  }
}

export default withStyles(styles)(JobLogs);
